(() => {
  'use strict';

  const OMEGA_ROOT_SELECTOR = '[id*="quantity_breaks_app_block"]';
  const OMEGA_WIDGET_SELECTOR = '.omg-card';
  const OMEGA_CARD_SELECTOR = '.omg-card-item';
  const OMEGA_ACTIVE_CARD_SELECTOR = '.omg-card-item-active';
  const OMEGA_DISABLED_CARD_SELECTOR =
    '.omg-card-item-disabled, [aria-disabled="true"]';

  let repairScheduled = false;
  let started = false;
  let discoveryObserver = null;

  /**
   * Return every Omega root contained by a document, element, or section.
   */
  function getOmegaRoots(scope = document) {
    const roots = new Set();

    if (
      scope instanceof Element &&
      scope.matches(OMEGA_ROOT_SELECTOR)
    ) {
      roots.add(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      scope
        .querySelectorAll(OMEGA_ROOT_SELECTOR)
        .forEach((root) => roots.add(root));
    }

    return Array.from(roots);
  }

  /**
   * An Omega root is usable only when it contains a real rendered card widget
   * with at least one quantity-tier card.
   *
   * Empty app blocks, inactive campaigns and placeholder blocks do not qualify.
   */
  function isUsableOmegaRoot(root) {
    if (!(root instanceof Element)) {
      return false;
    }

    return Array.from(
      root.querySelectorAll(OMEGA_WIDGET_SELECTOR)
    ).some((widget) =>
      Boolean(widget.querySelector(OMEGA_CARD_SELECTOR))
    );
  }

  function getUsableOmegaRoots(scope = document) {
    return getOmegaRoots(scope).filter(isUsableOmegaRoot);
  }

  /**
   * Return only genuine Omega card widgets containing quantity-tier cards.
   */
  function getUsableOmegaWidgets(scope = document) {
    return getUsableOmegaRoots(scope).flatMap((root) =>
      Array.from(
        root.querySelectorAll(OMEGA_WIDGET_SELECTOR)
      ).filter((widget) =>
        Boolean(widget.querySelector(OMEGA_CARD_SELECTOR))
      )
    );
  }

  /**
   * Return the first available tier in Omega's rendered order.
   * Omega renders its quantity rules from lowest to highest, so no quantity
   * text or hard-coded minimum needs to be parsed here.
   */
  function getFallbackCard(widget) {
    return Array.from(widget.querySelectorAll(OMEGA_CARD_SELECTOR)).find(
      (card) =>
        !card.matches(OMEGA_DISABLED_CARD_SELECTOR) &&
        !card.hidden
    );
  }

  /**
   * Ask Omega to select its first available tier.
   *
   * This never adds active classes, changes quantity inputs, or calculates
   * prices itself. Clicking the card lets Omega perform all of that work.
   */
  function restoreSelection(widget) {
    if (
      !widget ||
      !widget.isConnected ||
      !widget.querySelector(OMEGA_CARD_SELECTOR) ||
      widget.querySelector(OMEGA_ACTIVE_CARD_SELECTOR)
    ) {
      return;
    }

    const fallbackCard = getFallbackCard(widget);

    if (!fallbackCard) {
      return;
    }

    fallbackCard.click();

    /*
     * Normally Omega updates synchronously. The follow-up check covers a rare
     * mismatch where Omega internally still considered the first card active
     * even though its rendered active class was missing.
     */
    requestAnimationFrame(() => {
      if (
        widget.isConnected &&
        fallbackCard.isConnected &&
        widget.querySelector(OMEGA_CARD_SELECTOR) &&
        !widget.querySelector(OMEGA_ACTIVE_CARD_SELECTOR)
      ) {
        fallbackCard.click();
      }
    });
  }

  function restoreAllMissingSelections() {
    getUsableOmegaWidgets(document).forEach(restoreSelection);
  }

  /**
   * Wait until Omega has finished its current DOM update before checking it.
   */
  function scheduleRepair() {
    if (repairScheduled) {
      return;
    }

    repairScheduled = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        repairScheduled = false;
        restoreAllMissingSelections();
      });
    });
  }

  /**
   * Find only usable Omega roots associated with a particular product form.
   *
   * A root without both a card widget and at least one tier card is ignored.
   */
  function findOmegaRootsForForm(form) {
    const scopeSelectors = [
      '[id^="ProductInfo-"]',
      'product-info',
      '.product__info-container',
      '.product',
      'section',
      '.shopify-section',
    ];

    for (const selector of scopeSelectors) {
      const scope = form.closest(selector);

      if (!scope) {
        continue;
      }

      const roots = getUsableOmegaRoots(scope);

      if (roots.length > 0) {
        return roots;
      }
    }

    return [];
  }

  function isAddToCartForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    const action = form.getAttribute('action') || '';

    return /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(action);
  }

  /**
   * Treat an active Omega card like a radio button.
   *
   * This runs during capture, before Omega's bubbling React onClick handler.
   * It handles mouse, touch-generated clicks, and keyboard-generated clicks,
   * but deliberately does not cancel touch or pointer movement events.
   */
  function handleClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const card = event.target.closest(OMEGA_CARD_SELECTOR);

    if (
      !card ||
      !card.classList.contains(
        OMEGA_ACTIVE_CARD_SELECTOR.slice(1)
      )
    ) {
      return;
    }

    const omegaRoot = card.closest(OMEGA_ROOT_SELECTOR);

    if (!omegaRoot || !isUsableOmegaRoot(omegaRoot)) {
      return;
    }

    const widget = card.closest(OMEGA_WIDGET_SELECTOR);

    if (
      !widget ||
      !widget.querySelector(OMEGA_CARD_SELECTOR)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Final fail-safe: never allow a product form with a genuine Omega quantity
   * widget to submit while that widget has no active tier.
   *
   * Empty or inactive Omega app blocks are ignored completely.
   */
  function handleSubmit(event) {
    const form = event.target;

    if (!isAddToCartForm(form)) {
      return;
    }

    const omegaRoots = findOmegaRootsForForm(form);

    /*
     * No genuine card-based Omega widget belongs to this form.
     * Allow Shopify and the artwork-upload app to behave normally.
     */
    if (omegaRoots.length === 0) {
      return;
    }

    const usableWidgets = omegaRoots.flatMap((root) =>
      getUsableOmegaWidgets(root)
    );

    /*
     * This should only occur during an Omega rerender. If there are no usable
     * widgets, do not intercept the form.
     */
    if (usableWidgets.length === 0) {
      return;
    }

    const invalidWidgets = usableWidgets.filter(
      (widget) =>
        !widget.querySelector(OMEGA_ACTIVE_CARD_SELECTOR)
    );

    if (invalidWidgets.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    invalidWidgets.forEach(restoreSelection);
  }

  /**
   * Collect Omega roots directly involved in a DOM mutation.
   */
  function getMutationOmegaRoots(mutation) {
    const roots = new Set();

    if (mutation.target instanceof Element) {
      const targetRoot = mutation.target.closest(
        OMEGA_ROOT_SELECTOR
      );

      if (targetRoot) {
        roots.add(targetRoot);
      }
    }

    [...mutation.addedNodes, ...mutation.removedNodes].forEach(
      (node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches(OMEGA_ROOT_SELECTOR)) {
          roots.add(node);
        }

        const containingRoot = node.closest(OMEGA_ROOT_SELECTOR);

        if (containingRoot) {
          roots.add(containingRoot);
        }

        node
          .querySelectorAll(OMEGA_ROOT_SELECTOR)
          .forEach((root) => roots.add(root));
      }
    );

    return Array.from(roots);
  }

  /**
   * Empty Omega roots do not count as relevant guard mutations.
   */
  function mutationTouchesUsableOmega(mutation) {
    return getMutationOmegaRoots(mutation).some(
      isUsableOmegaRoot
    );
  }

  /**
   * Start the permanent guard only after a genuine card widget is present.
   */
  function start() {
    if (started) {
      return;
    }

    if (getUsableOmegaRoots(document).length === 0) {
      return;
    }

    started = true;

    if (discoveryObserver) {
      discoveryObserver.disconnect();
      discoveryObserver = null;
    }

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);

    /*
     * Supports Omega widget replacement after variant changes.
     * Empty and inactive Omega blocks cannot trigger a repair.
     */
    const observer = new MutationObserver((mutations) => {
      if (mutations.some(mutationTouchesUsableOmega)) {
        scheduleRepair();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    scheduleRepair();
  }

  /**
   * Initialise immediately if a genuine Omega card widget already exists.
   * Otherwise, wait efficiently for real tier cards to be rendered.
   *
   * Empty app blocks do not initialise the main guard.
   */
  function startOrWaitForOmega() {
    if (getUsableOmegaRoots(document).length > 0) {
      start();
      return;
    }

    if (discoveryObserver) {
      return;
    }

    discoveryObserver = new MutationObserver((mutations) => {
      if (!mutations.some(mutationTouchesUsableOmega)) {
        return;
      }

      /*
       * Confirm that a usable root is currently connected to the document.
       * This prevents a removed or detached block from starting the guard.
       */
      if (getUsableOmegaRoots(document).length === 0) {
        return;
      }

      discoveryObserver.disconnect();
      discoveryObserver = null;
      start();
    });

    discoveryObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    /*
     * Close the small race between the initial query and observer attachment.
     */
    if (getUsableOmegaRoots(document).length > 0) {
      discoveryObserver.disconnect();
      discoveryObserver = null;
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startOrWaitForOmega,
      { once: true }
    );
  } else {
    startOrWaitForOmega();
  }
})();